/**
 * 题目类型与难度标签 - 统一管理
 * 供 admin、web、backend 等消费，保证展示一致
 */

/** 题目作答形态展示名（与 QuestionType 枚举一致） */
export const QUESTION_TYPE_LABELS: Record<string, string> = {
  single_choice: '单选题',
  multiple_choice: '多选题',
  true_false: '判断题',
  fill_blank: '填空题',
};

/** 材料轴展示名 */
export const QUESTION_STIMULUS_LABELS: Record<string, string> = {
  audio: '音频',
  passage: '篇章',
  image: '图片',
};

/** 题目类型下拉选项 */
export const QUESTION_TYPE_OPTIONS = Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/** 题目类型下拉选项（含“全部类型”，用于筛选器） */
export const QUESTION_TYPE_OPTIONS_WITH_ALL = [
  { value: '', label: '全部类型' },
  ...QUESTION_TYPE_OPTIONS,
];

/** 题目难度可选值（CEFR 等级，与 exams 中 CEFR 类考试配置一致） */
export const CEFR_LEVEL_VALUES = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

export type CEFRLevel = (typeof CEFR_LEVEL_VALUES)[number];

/** 题目难度展示名（CEFR 等级） */
export const LEVEL_LABELS: Record<string, string> = {
  A1: 'A1 初级',
  A2: 'A2 基础',
  B1: 'B1 中级',
  B2: 'B2 中高级',
  C1: 'C1 高级',
  C2: 'C2 精通',
};

/** 难度下拉选项（含“不限定/全部”；表单中空值可表示“不设置”） */
export const LEVEL_OPTIONS = [
  { value: '', label: '不限定' },
  ...CEFR_LEVEL_VALUES.map((value) => ({ value, label: LEVEL_LABELS[value] })),
];

/** 难度下拉选项（编辑场景，空值显示“不设置”） */
export const LEVEL_OPTIONS_EDIT = [
  { value: '', label: '不设置' },
  ...LEVEL_OPTIONS.filter((o) => o.value !== ''),
];

/**
 * 获取题目类型展示名
 */
export function getQuestionTypeLabel(typeId: string): string {
  return QUESTION_TYPE_LABELS[typeId] ?? typeId;
}

/**
 * 获取题目难度展示名
 */
export function getLevelLabel(level: string | undefined | null): string {
  if (level == null || level === '') return '未设置';
  return LEVEL_LABELS[level] ?? level;
}

/**
 * Skill / section 短标签（无 Exam 上下文时的回退；有 Exam 时优先用
 * `@examflow/learning-space` 的 `getLearningModuleLabel`）。
 */
export const SKILL_SECTION_LABELS: Record<string, string> = {
  listening: '听力理解',
  language_structures: '语言结构',
  reading: '阅读理解',
  writing: '写作',
  speaking: '口语',
  grammar: '语法',
  vocabulary: '词汇',
  use_of_english: '语言运用',
};

export function getSkillSectionLabel(sectionId: string | undefined | null): string {
  if (!sectionId) return '综合能力';
  return SKILL_SECTION_LABELS[sectionId] ?? sectionId;
}
